import { Asset, AssetState, FolderContent } from '@/api/generatedApi';
import { AssetActionRow } from '@/components/AssetActionRow';
import { Thumbnail } from '@/components/Thumbnail';
import { useAssetPolling } from '@/hooks/useDataPolling/useAssetPolling';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { getDepthColor } from '@/pages/TaxonomyPage/TaxonomyTree/taxonomyTreeConstants';
import { useTenants } from '@/services/tenants/useTenants';
import { getAssetName } from '@/utils/assets';
import { Label } from '@mui/icons-material';
import { Box, Chip, LinearProgress, Tooltip, Typography } from '@mui/material';
import { getFileExtension } from '@physna/supported-files';
import { useDraggable } from '@dnd-kit/core';
import { DND_NO_SELECT_STYLES, DND_OPACITY } from '@/components/dnd';
import { useBulkSelectionContext } from '@/components/ContentsTable/BulkSelectionContext';
import { memo, useMemo } from 'react';
import { InlineLink, MediaArea, StyledCard, StyledCardContent, ThumbnailLink } from './cardStyles';

const THUMBNAIL_SIZE = 160;

const IndexingIndicator = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
    <Typography variant='caption' color='text.secondary'>
      Indexing
    </Typography>
    <LinearProgress sx={{ width: 80 }} />
  </Box>
);

const AssetCardVisual = memo(function AssetCardVisual({ asset, assetPath }: { asset: Asset; assetPath: string }) {
  const { isTaxonomyEnabled } = useFeatureFlags();
  const extension = getFileExtension(asset.path);
  const isIndexing = asset.state === AssetState.Indexing;

  return (
    <>
      <MediaArea>
        {isIndexing ? (
          <IndexingIndicator />
        ) : (
          <ThumbnailLink to={assetPath} draggable={false} sx={{ width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE }}>
            <Thumbnail asset={asset} size={THUMBNAIL_SIZE} noLink />
          </ThumbnailLink>
        )}
      </MediaArea>
      <StyledCardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <InlineLink to={assetPath} draggable={false} sx={{ display: 'block', overflow: 'hidden', minWidth: 0 }}>
            <Typography
              variant='body2'
              component='span'
              sx={{
                fontWeight: 600,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {getAssetName(asset)}
            </Typography>
          </InlineLink>
          {extension && <Chip label={extension} size='small' variant='outlined' sx={{ flexShrink: 0 }} />}
          {isTaxonomyEnabled && asset.label?.summary && (
            <Tooltip title={asset.label.summary.name} arrow>
              <Chip
                icon={<Label sx={{ fontSize: 14, color: `${getDepthColor(asset.label.summary.depth)} !important` }} />}
                size='small'
                variant='outlined'
                sx={{
                  flexShrink: 0,
                  borderColor: getDepthColor(asset.label.summary.depth),
                  '& .MuiChip-label': { display: 'none' },
                  '& .MuiChip-icon': { mx: 'auto' },
                  width: 28,
                }}
              />
            </Tooltip>
          )}
        </Box>
      </StyledCardContent>
      {!isIndexing && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', px: 1.5, py: 0.5 }}>
          <AssetActionRow
            asset={asset}
            hideAssetPageLink
            slim
            sx={{
              justifyContent: 'center',
              '& > *': { display: 'inline-flex', alignItems: 'center' },
              '& .MuiIconButton-root': { padding: '6px' },
              '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
            }}
          />
        </Box>
      )}
    </>
  );
});

export const AssetContentCard = ({
  asset: rawAsset,
  onContextMenu,
}: {
  asset: Asset;
  onContextMenu?: (event: React.MouseEvent) => void;
}) => {
  const { getPath } = useTenants();
  const { data: asset } = useAssetPolling({ row: rawAsset as FolderContent });
  const bulkSelection = useBulkSelectionContext<FolderContent>();

  const selectedRows = bulkSelection?.selectedRows ?? [];
  const isSelected = selectedRows.some(r => r.id === asset.id);
  const singleItemToDrag = useMemo(() => [{ ...asset, contentType: 'asset' } as FolderContent], [asset]);
  const contentsToDrag = isSelected && selectedRows.length > 0 ? selectedRows : singleItemToDrag;

  const {
    attributes,
    listeners,
    setNodeRef: setDragReference,
    isDragging,
  } = useDraggable({
    id: `drag-card-${asset.id}`,
    data: { contents: contentsToDrag },
  });

  const cardSx = useMemo(
    () => ({
      opacity: isDragging ? DND_OPACITY.dragging : DND_OPACITY.normal,
      ...DND_NO_SELECT_STYLES,
    }),
    [isDragging]
  );

  return (
    <StyledCard
      data-id={asset.id}
      data-field='name'
      onContextMenu={onContextMenu}
      ref={setDragReference}
      {...attributes}
      {...listeners}
      sx={cardSx}
    >
      <AssetCardVisual asset={asset} assetPath={getPath(`asset/${asset.id}`)} />
    </StyledCard>
  );
};
